import { useState, useCallback, ReactNode } from 'react';
import { Card, Button, ProgressInfo, Skeleton, useSpinner, InfoTooltip } from '@chikara/ui';
import { formatMXN } from '@/features/dashboard/utils/dashboard.utils';
import type { DashboardSummaryProps } from '@/features/dashboard/types/dashboard.types';
import { MakePaymentModal } from '@/features/credits/components/MakePaymentModal';
import { hasPermission } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import styles from './DashboardSummary.module.css';
import { t } from '@/i18n';
import { creditService } from '@/features/credits/services/creditService/creditService';
import type { LinkedAccount } from '@/features/credits/types/credits.types';



interface InfoRowProps {
  label: ReactNode;
  value: ReactNode;
  isLast?: boolean;
  className?: string;
}

/** Etiqueta con ícono de ayuda (?) y su tooltip. */
const LabelWithTooltip = ({ text, tooltip }: { text: string; tooltip: string }) => (
  <span className={styles.labelWithInfo}>
    {text}
    <InfoTooltip content={tooltip} label={text} />
  </span>
);

const InfoRow = ({ label, value, isLast, className }: InfoRowProps) => (
  <div className={`${styles.fullWidthRow} ${className || ''} ${isLast ? styles.noBorder : ''}`}>
    <span className={styles.infoLabel}>{label}</span>
    <span className={styles.infoValue}>{value}</span>
  </div>
);

export function DashboardSummary({ summary, userSpending }: DashboardSummaryProps) {
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  const [fetchedLinkedAccount, setFetchedLinkedAccount] = useState<LinkedAccount | null>(null);
  const { userData, payload: authPayload } = useAuth();
  const { isSpinnerLoading, show, hide } = useSpinner();

  const handleMakePayment = useCallback(async () => {
    if (!authPayload?.customer_uuid) return;

    show();
    try {
      const now = new Date();
      const monthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      const response = await creditService.getSummary(authPayload.customer_uuid, monthYear);
      if (response.data?.deposit_info) {
        setFetchedLinkedAccount({
          accountName: response.data.deposit_info.account_tag || 'Sin asignar',
          clabe: response.data.deposit_info.clabe || 'Sin asignar',
          bankName: response.data.deposit_info.bank_name || 'Sin asignar',
        });
      }
      setIsPaymentModalOpen(true);
    } catch (err) {
      console.error('Error fetching linked account:', err);
    } finally {
      hide();
    }
  }, [authPayload?.customer_uuid, show, hide]);

  const handleClosePaymentModal = useCallback(() => {
    if (!isConfirmingPayment) {
      setIsPaymentModalOpen(false);
    }
  }, [isConfirmingPayment]);

  const handleConfirmPayment = useCallback(async () => {
    setIsConfirmingPayment(true);
    // TODO: llamar al servicio de pago
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsConfirmingPayment(false);
    setIsPaymentModalOpen(false);
  }, []);


  const [isCreditExpanded, setIsCreditExpanded] = useState(true);
  const [isSpentExpanded, setIsSpentExpanded] = useState(true);

  const renderDesktopSkeletons = () => (
    <>
      <Card className={styles.creditInfoField}>
        <div className={styles.skeletonContainer}>
          <Skeleton width="100px" height="14px" />
          <Skeleton width="150px" height="24px" />
        </div>
      </Card>
      <Card className={`${styles.creditInfoField} ${styles.flex2}`}>
        <div className={styles.skeletonRow}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.skeletonItem}>
              <Skeleton width="80px" height="14px" />
              <Skeleton width="120px" height="24px" />
            </div>
          ))}
        </div>
      </Card>
    </>
  );

  const renderUserSpendingSkeletons = () => (
    <>
      <Card className={`${styles.creditInfoField} ${styles.limitCard}`}>
        <div className={styles.skeletonContainer}>
          <Skeleton width="80px" height="14px" />
          <Skeleton width="120px" height="24px" />
        </div>
      </Card>
      <Card className={`${styles.progressCard} ${styles.creditInfoField}`}>
        <div className={styles.progressSkeleton}>
          <Skeleton width="120px" height="14px" />
          <Skeleton width="100%" height="12px" />
          <div className={styles.progressSkeletonLabels}>
            <Skeleton width="60px" height="14px" />
            <Skeleton width="60px" height="14px" />
          </div>
        </div>
      </Card>
    </>
  );

  const renderUserSpendingProgress = () => (
    <ProgressInfo
      total={userSpending?.limit || 0}
      spent={userSpending?.spent || 0}
      formatValue={formatMXN}
      spentTooltip={t('tooltips.userSpent')}
      availableTooltip={t('tooltips.userAvailable')}
    />
  );

  return (
    <>
      {/* Mobile Version */}
      <div className={styles.mobileSection}>
        {/* Card 1: Línea de Crédito */}
        {hasPermission(userData, 'VIEW_DASHBOARD_SUMMARY') && (
          <div className={styles.mobileCard}>
            <div className={styles.mobileCardHeader}>
              <h3 className={styles.mobileCardTitle}>{t('dashboard.creditSectionTitle')}</h3>
              <button 
                className={styles.toggleButton}
                onClick={() => setIsCreditExpanded(!isCreditExpanded)}
              >
                {isCreditExpanded ? t('common.viewLess') : t('common.viewMore')}
              </button>
            </div>
            
            {isCreditExpanded && (
              <div className={styles.expandedContent}>
                <InfoRow label={<LabelWithTooltip text={t('dashboard.creditLineLabel')} tooltip={t('tooltips.creditLine')} />} value={formatMXN(summary?.creditLine || 0)} />

                <div className={styles.sideBySideRow}>
                  <div className={styles.halfWidthColWithBorder}>
                    <span className={styles.infoLabel}>
                      <LabelWithTooltip text={t('dashboard.spentLabel')} tooltip={t('tooltips.spentCompany')} />
                    </span>
                    <span className={styles.infoValue}>{formatMXN(summary?.spent || 0)}</span>
                  </div>
                  <div className={styles.halfWidthCol}>
                    <span className={styles.infoLabel}>
                      <LabelWithTooltip text={t('dashboard.disposableLabel')} tooltip={t('tooltips.availableCompany')} />
                    </span>
                    <span className={styles.infoValue}>{formatMXN(summary?.disposable || 0)}</span>
                  </div>
                </div>

                <div className={`${styles.fullWidthRow} ${styles.noBorder}`}>
                  <div className={styles.toPayLabel}>
                    <LabelWithTooltip text={t('dashboard.toPayLabel')} tooltip={t('tooltips.toPay')} />{' '}
                    <span className={styles.paymentDate}>{summary?.paymentDate}</span>
                  </div>
                  <div className={styles.toPayContainer}>
                    <div className={styles.toPayValue}>
                      {formatMXN(summary?.toPay || 0)}
                    </div>
                    <button className={styles.redToggleButton} onClick={handleMakePayment}>
                      {t('dashboard.payAction')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Card 2: Tu Gasto */}
        <div className={styles.mobileCard}>
          <div className={styles.mobileCardHeader}>
            <h3 className={styles.mobileCardTitle}>{t('dashboard.userSpendingTitle')}</h3>
            <button 
              className={styles.toggleButton}
              onClick={() => setIsSpentExpanded(!isSpentExpanded)}
            >
              {isSpentExpanded ? t('common.viewLess') : t('common.viewMore')}
            </button>
          </div>

          {isSpentExpanded && (
            <div className={styles.expandedContent}>
              <InfoRow label={<LabelWithTooltip text={t('dashboard.userLimitLabel')} tooltip={t('tooltips.userLimit')} />} value={formatMXN(userSpending?.limit || 0)} />
              <InfoRow label={t('dashboard.userSpentLabel')} value={formatMXN(userSpending?.spent || 0)} />
              <InfoRow label={<LabelWithTooltip text={t('dashboard.disposableLabel')} tooltip={t('tooltips.userAvailable')} />} value={formatMXN((userSpending?.limit || 0) - (userSpending?.spent || 0))} />
              
              <div className={styles.progressiveValue}>
                {renderUserSpendingProgress()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Version */}
      {hasPermission(userData, 'VIEW_DASHBOARD_SUMMARY') && (
        <section className={styles.section}>
          <h2 className={styles.subsectionTitle}>{t('dashboard.creditSectionTitle')}</h2>
          <div className={styles.creditGrid}>
            {isSpinnerLoading ? renderDesktopSkeletons() : (
              <>
                <Card className={styles.creditInfoField}>
                  <Card.Stats
                    label={<LabelWithTooltip text={t('dashboard.creditLineLabel')} tooltip={t('tooltips.creditLine')} />}
                    value={formatMXN(summary?.creditLine || 0)}
                    align="center"
                  />
                </Card>
                
                <Card orientation="horizontal" className={styles.creditInfoField}>
                  <Card.Stats
                    label={<LabelWithTooltip text={t('dashboard.spentLabel')} tooltip={t('tooltips.spentCompany')} />}
                    value={formatMXN(summary?.spent || 0)}
                    align="center"
                  />
                  <Card.Stats
                    label={<LabelWithTooltip text={t('dashboard.disposableLabel')} tooltip={t('tooltips.availableCompany')} />}
                    value={formatMXN(summary?.disposable || 0)}
                    align="center"
                  />
                  <Card.Stats
                    label={
                      <span className={styles.paymentLabel}>
                        <LabelWithTooltip text={t('dashboard.toPayLabel')} tooltip={t('tooltips.toPay')} />{' '}
                        <span className={styles.paymentDate}>{summary?.paymentDate}</span>
                      </span>
                    }
                    value={formatMXN(summary?.toPay || 0)}
                    action={
                      <Button variant="link-tertiary" size="md" onClick={handleMakePayment}>
                        {t('dashboard.payAction')}
                      </Button>
                    }
                  />                   
                </Card>
              </>
            )}
          </div>
        </section>
      )}

      {/* Sección de Tu gasto como usuario */}
      <section className={styles.section}>
        <h2 className={styles.subsectionTitle}>{t('dashboard.userSpendingTitle')}</h2>
        <div className={styles.userSpendingContainer}>
          {isSpinnerLoading ? renderUserSpendingSkeletons() : (
            <>
              <Card className={`${styles.creditInfoField} ${styles.limitCard}`}>
                <Card.Stats
                  label={<LabelWithTooltip text={t('dashboard.userLimitLabel')} tooltip={t('tooltips.userLimit')} />}
                  value={formatMXN(userSpending?.limit || 0)}
                  align="center"
                />
              </Card>
              
              <Card className={`${styles.progressCard} ${styles.creditInfoField}`}>
                <Card.Stats
                  label={t('dashboard.userSpentLabel')}
                  align="center"
                  className={styles.progressStats}
                  value={
                    <div className={styles.fullWidth}>
                      {renderUserSpendingProgress()}
                    </div>
                  }
                />
              </Card>
            </>
          )}
        </div>

        <MakePaymentModal
          isOpen={isPaymentModalOpen}
          onClose={handleClosePaymentModal}
          amount={summary?.toPay || 0}
          linkedAccount={fetchedLinkedAccount}
          onConfirm={handleConfirmPayment}
          isConfirming={isConfirmingPayment}
        />
      </section>
    </>
  );
}
